// Basic
import { useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import actionsSims from '../../../../api/actionsSims';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import FooterStep from '@/common-utils/components/renders/modal/components/footers/FooterStep/FooterStep';
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import AddSimCardsByProvider from '../../addSimCard/components/AddSimCardsByProvider';
import SimCardsTable from '../../../tables/SimCardsTable';
import SelectSimSelectionMode from '../components/SelectSimSelectionMode';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
// Common
import getInitialValueForRowSelected from '@/common-utils/components/renders/dataTable/utils/GetInitialValueForRowSelected';
import splitLines from '../../../../funcs/splitLines';
import { useToast } from '@/common-utils/hooks/use-toast';
import useStepConfig from '@/common-utils/hooks/useStepConfig';
// Lang
import { nsSimCard } from '@/app/i18n/namespaces';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';
/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.setLaunchRefetch
 * @returns {JSX} CustomPayModalUnassignSimCards
 */
function CustomPayModalUnassignSimCards({ isOpen, setIsOpen, setLaunchRefetch }) {

    // Toast
    const { toast } = useToast();

    // Constants
    const STEPS_NUMBER = 3;
    const STEP_TITLE_PREFIX = 'modal.unassign.steps';

    // State
    const [step, setStep] = useState(0);
    const [simCardsSelected, setSimCardsSelected] = useState(getInitialValueForRowSelected());
    const [actionSelected, setActionSelected] = useState('');
    const [simNumberList, setSimNumberList] = useState('');
    const [isInvalidSimInput, setIsInvalidSimInput] = useState(false);

    // API Calls Assign SimCard
    const onSuccess = () => {
        setIsOpen(false);
        setLaunchRefetch(true);
        toast({
            description: <Entity entity={'modal.unassign.success'} namespace={nsSimCard} />,
            variant: 'success'
        });
    };
    const onError = () => {
        setIsOpen(false);
        toast({
            description: <Entity entity={'modal.unassign.error'} namespace={nsSimCard} />,
            variant: 'danger'
        });
    };
    const submitUnassignSimCards = actionsSims.reassignAndUnassignSimCards(onSuccess, onError);

    // Hook
    const { stepTitle, disabledNext } = useStepConfig(
        step,
        () => ({
            0: {
                stepTitle: `${STEP_TITLE_PREFIX}.action_selection`,
                disabledNext: !actionSelected
            },
            1: {
                stepTitle: `${STEP_TITLE_PREFIX}.sims_selection`,
                disabledNext: (actionSelected === 'select' && simCardsSelected.rowsData.length === 0)
                    || (actionSelected === 'input' && isInvalidSimInput)
            },
            2: {
                stepTitle: `${STEP_TITLE_PREFIX}.confirmation`,
                disabledNext: false
            }
        }),
        [simCardsSelected, actionSelected, simNumberList, isInvalidSimInput]
    );

    // Render
    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className={actionSelected === 'select' && step === 1 ? 'max-w-6xl' : 'max-w-lg'}>
                <HeaderTitle title={'modal.unassign.title'} namespace={nsSimCard} />
                <ModalBody className='modal-max-height overflow-auto'>
                    <Entity
                        entity={stepTitle}
                        data={{ step: step + 1, maxStep: STEPS_NUMBER }}
                        namespace={nsSimCard}
                        className='mb-2 flex'
                    />
                    {
                        step === 0 &&
                        <div>
                            <Entity entity={'modal.unassign.labels.action'} namespace={nsSimCard} />
                            <SelectSimSelectionMode actionSelected={actionSelected} setActionSelected={setActionSelected} />
                        </div>
                    }
                    {
                        step === 1 &&
                        <>
                            {
                                actionSelected === 'select' ?
                                    <SimCardsTable
                                        filtersEventName={setFiltersEventName('sim_cards', true)}
                                        rowsSelected={simCardsSelected}
                                        setRowsSelected={setSimCardsSelected}
                                    />
                                    :
                                    <AddSimCardsByProvider
                                        simNumberList={simNumberList}
                                        setSimNumberList={setSimNumberList}
                                        setDisableSubmit={setIsInvalidSimInput}
                                    />
                            }
                        </>
                    }
                    {
                        step === 2 &&
                        <Entity entity={'modal.unassign.text.confirmation'} namespace={nsSimCard} />
                    }
                </ModalBody>
                <FooterStep
                    step={step}
                    stepsNumber={STEPS_NUMBER}
                    setStep={setStep}
                    onValidate={() => {
                        submitUnassignSimCards.mutate({
                            simsList: splitLines(simNumberList)
                        });
                    }}
                    disableValidation={disabledNext}
                    isLoading={submitUnassignSimCards.isLoading}
                />
            </ModalContent>
        </Modal>
    );
}

CustomPayModalUnassignSimCards.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    setLaunchRefetch: PropTypes.func.isRequired
};

export default CustomPayModalUnassignSimCards;